import { Booking, Resource, WorkingHoursProfile, Absence, BankHoliday } from '@/types';
import { eachDayOfInterval, getDay, isSameDay, parseISO } from 'date-fns';

/**
 * Get the day key for WorkingHoursProfile based on a date
 */
function getDayKey(date: Date): keyof WorkingHoursProfile {
  const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  return dayKeys[dayOfWeek];
}

/**
 * Get working hours for a resource on a specific day
 * Uses custom profile if enabled, otherwise falls back to global profile
 */
function getWorkingHoursForDay(
  resource: Resource | undefined,
  date: Date,
  globalWorkingProfile: WorkingHoursProfile | undefined
): number {
  const dayKey = getDayKey(date);
  
  // If resource has custom profile enabled and has a custom profile, use it
  if (resource?.useCustomWorkingProfile && resource.customWorkingProfile) {
    return resource.customWorkingProfile[dayKey] || 0;
  }
  
  // Otherwise use global working profile if available
  if (globalWorkingProfile) {
    return globalWorkingProfile[dayKey] || 0;
  }
  
  // Default fallback: 8 hours on weekdays, 0 on weekends
  const dayOfWeek = getDay(date);
  return (dayOfWeek >= 1 && dayOfWeek <= 5) ? 8 : 0;
}

/**
 * Calculate absence minutes for a resource on a specific day
 */
function calculateAbsenceMinutesForDay(
  resourceId: string,
  date: Date,
  absences: Absence[],
  workingHoursPerDay: number
): number {
  let absenceMinutes = 0;

  // Filter absences for this resource on this day
  const dayAbsences = absences.filter((absence) => {
    const absenceStart = parseISO(absence.startDate);
    const absenceEnd = parseISO(absence.endDate);
    
    return (
      String(absence.resourceId) === String(resourceId) &&
      isSameDay(date, absenceStart) || 
      isSameDay(date, absenceEnd) ||
      (date >= absenceStart && date <= absenceEnd)
    );
  });

  dayAbsences.forEach((absence) => {
    switch (absence.type) {
      case 'full-day':
        absenceMinutes += workingHoursPerDay * 60;
        break;
      case 'half-day-am':
      case 'half-day-pm':
        absenceMinutes += (workingHoursPerDay * 60) / 2;
        break;
      case 'hourly':
        if (absence.startTime && absence.endTime) {
          const [startHour, startMin] = absence.startTime.split(':').map(Number);
          const [endHour, endMin] = absence.endTime.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          absenceMinutes += endMinutes - startMinutes;
        }
        break;
    }
  });

  return absenceMinutes;
}

/**
 * Check if a date is a bank holiday
 */
function checkIsBankHoliday(date: Date, bankHolidays: BankHoliday[]): boolean {
  return bankHolidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(date, holidayDate);
  });
}

/**
 * Calculate resource utilization percentage for a given time period
 * Now supports working profiles (global and custom), excludes cancelled bookings,
 * and includes absences and bank holidays in the calculation
 */
export function calculateResourceUtilization(
  resourceId: string,
  bookings: Booking[],
  startDate: Date,
  endDate: Date,
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile,
  absences?: Absence[],
  bankHolidays?: BankHoliday[]
): number {
  // Filter bookings for this resource within the date range
  // Exclude cancelled bookings
  const resourceBookings = bookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    // Exclude cancelled bookings
    if (booking.status === 'cancelled') {
      return false;
    }
    
    return (
      String(booking.resourceId) === String(resourceId) &&
      bookingStart < endDate &&
      bookingEnd > startDate
    );
  });

  // Calculate total available minutes based on working profile
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  let totalAvailableMinutes = 0;
  let totalBookedMinutes = 0;
  
  days.forEach((day) => {
    const workingHours = getWorkingHoursForDay(resource, day, globalWorkingProfile);
    
    // Skip non-working days
    if (workingHours === 0) {
      return;
    }
    
    // Check if this day is a bank holiday - if so, count as fully utilized
    if (bankHolidays && checkIsBankHoliday(day, bankHolidays)) {
      totalAvailableMinutes += workingHours * 60;
      totalBookedMinutes += workingHours * 60; // Bank holiday counts as 100% utilized
      return;
    }
    
    totalAvailableMinutes += workingHours * 60;
    
    // Add absence minutes for this day
    if (absences) {
      totalBookedMinutes += calculateAbsenceMinutesForDay(resourceId, day, absences, workingHours);
    }
  });

  // If no available minutes (all non-working days), return 0
  if (totalAvailableMinutes === 0) {
    return 0;
  }

  // Calculate booking minutes
  resourceBookings.forEach((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    // For full-day bookings, calculate based on working hours for each day
    if (booking.durationType === 'full-day') {
      // Get the days this booking spans within the date range
      const bookingDays = eachDayOfInterval({
        start: bookingStart < startDate ? startDate : bookingStart,
        end: bookingEnd > endDate ? endDate : bookingEnd
      });
      
      bookingDays.forEach((day) => {
        // Skip bank holidays as they're already counted
        if (bankHolidays && checkIsBankHoliday(day, bankHolidays)) {
          return;
        }
        const workingHours = getWorkingHoursForDay(resource, day, globalWorkingProfile);
        totalBookedMinutes += workingHours * 60;
      });
    } else if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
      // Half-day booking uses half of the working hours for that day
      const workingHours = getWorkingHoursForDay(resource, bookingStart, globalWorkingProfile);
      totalBookedMinutes += (workingHours * 60) / 2;
    } else {
      // Hourly booking - calculate actual duration
      // Clamp booking times to the date range
      const effectiveStart = bookingStart < startDate ? startDate : bookingStart;
      const effectiveEnd = bookingEnd > endDate ? endDate : bookingEnd;
      
      const minutes = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);
      totalBookedMinutes += minutes;
    }
  });

  // If no bookings and no absences, return 0
  if (totalBookedMinutes === 0) return 0;

  const utilization = (totalBookedMinutes / totalAvailableMinutes) * 100;
  return Math.round(Math.min(utilization, 999)); // Cap at 999% for display
}

/**
 * Generate a consistent color for a project name
 */
export function getProjectColor(projectName: string): string {
  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#14b8a6', // teal
    '#a855f7', // violet
    '#84cc16', // lime
    '#f43f5e', // rose
  ];
  
  let hash = 0;
  for (let i = 0; i < projectName.length; i++) {
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Check if two bookings overlap
 */
export function checkBookingOverlap(
  booking1: { startTime: Date | string; endTime: Date | string },
  booking2: { startTime: Date | string; endTime: Date | string }
): boolean {
  const start1 = new Date(booking1.startTime);
  const end1 = new Date(booking1.endTime);
  const start2 = new Date(booking2.startTime);
  const end2 = new Date(booking2.endTime);

  return start1 < end2 && start2 < end1;
}

/**
 * Format duration in a human-readable way
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Generate time slots for booking forms
 */
export function generateTimeSlots(interval: number = 30): Array<{ hour: number; minute: number; label: string }> {
  const slots = [];
  
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      const period = hour < 12 ? 'AM' : 'PM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      
      slots.push({
        hour,
        minute,
        label: `${displayHour}:${minuteStr} ${period}`,
      });
    }
  }
  
  return slots;
}
